<template>
  <div class="system-widget box" :class="{ 'is-loading': loading }">
    <!-- Header -->
    <div class="system-widget-header">
      <span class="system-widget-title">{{ title || $t('systemInfo') }}</span>
      <button
        class="system-widget-refresh-btn"
        :disabled="loading"
        @click="refresh"
      >
        <octicon :icon="syncIcon" :class="{ 'spin': loading }" />
      </button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="notification is-danger is-light">
      <p>{{ $t('errorFetchingMetrics') }}</p>
      <p class="is-size-7">{{ error }}</p>
      <button class="button is-small mt-2" @click="refresh">
        <octicon :icon="syncIcon" />
        <span>{{ $t('retry') }}</span>
      </button>
    </div>

    <!-- Metrics Display -->
    <div v-else class="system-metrics">
      <!-- Disk Usage -->
      <div class="metric-card">
        <div class="metric-header">
          <octicon :icon="databaseIcon" />
          <span>{{ $t('diskUsage') }}</span>
        </div>
        <div class="metric-body">
          <div class="progress-container">
            <progress
              class="progress"
              :class="getProgressClass(disk.usedPercent)"
              :value="disk.usedPercent"
              max="100"
            />
            <span class="progress-label">{{ formatPercent(disk.usedPercent) }}</span>
          </div>
          <p class="metric-detail">
            {{ formatBytes(disk.used) }} / {{ formatBytes(disk.total) }}
            <span class="metric-available">({{ formatBytes(disk.available) }} {{ $t('available') }})</span>
          </p>
        </div>
      </div>

      <!-- CPU Usage -->
      <div class="metric-card">
        <div class="metric-header">
          <octicon :icon="cpuIcon" />
          <span>{{ $t('cpuUsage') }}</span>
        </div>
        <div class="metric-body">
          <div class="progress-container">
            <progress
              class="progress"
              :class="getProgressClass(cpu.usagePercent)"
              :value="cpu.usagePercent"
              max="100"
            />
            <span class="progress-label">{{ formatPercent(cpu.usagePercent) }}</span>
          </div>
          <p class="metric-detail">
            {{ cpu.cores }} Cores |
            Load: {{ formatLoad(cpu.load1) }}, {{ formatLoad(cpu.load5) }}, {{ formatLoad(cpu.load15) }}
          </p>
        </div>
      </div>

      <!-- RAM Usage -->
      <div class="metric-card">
        <div class="metric-header">
          <octicon :icon="serverIcon" />
          <span>{{ $t('ramUsage') }}</span>
        </div>
        <div class="metric-body">
          <div class="progress-container">
            <progress
              class="progress"
              :class="getProgressClass(ram.usedPercent)"
              :value="ram.usedPercent"
              max="100"
            />
            <span class="progress-label">{{ formatPercent(ram.usedPercent) }}</span>
          </div>
          <p class="metric-detail">
            {{ formatBytes(ram.used) }} / {{ formatBytes(ram.total) }}
            <span class="metric-available">({{ formatBytes(ram.available) }} {{ $t('available') }})</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="metric-footer">
      <span v-if="lastUpdated" class="metric-footer-item">
        {{ $t('lastUpdated') }}: {{ formatTime(lastUpdated) }}
      </span>
      <span v-if="autoRefresh && !error" class="metric-footer-item">
        {{ $t('autoRefreshIn') }} {{ nextRefreshIn }}s
      </span>
    </div>
  </div>
</template>

<script>
import { sync, database, server, circuitBoard } from 'octicons-vue'

export default {
  name: 'SystemWidget',
  props: {
    title: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: ''
    },
    refreshInterval: {
      type: Number,
      default: 30000 // 30 seconds
    },
    autoRefresh: {
      type: Boolean,
      default: true
    },
    mountpoint: {
      type: String,
      default: '/'
    }
  },
  data () {
    return {
      disk: { total: 0, used: 0, available: 0, usedPercent: 0 },
      cpu: { usagePercent: 0, cores: 0, load1: 0, load5: 0, load15: 0 },
      ram: { total: 0, used: 0, available: 0, usedPercent: 0 },
      lastUpdated: null,
      loading: false,
      error: null,
      interval: null,
      nextRefreshIn: 0,
      countdownInterval: null
    }
  },
  computed: {
    syncIcon () {
      return sync
    },
    databaseIcon () {
      return database
    },
    cpuIcon () {
      return circuitBoard
    },
    serverIcon () {
      return server
    },
    colorClass () {
      return this.color ? `is-color-${this.color}` : ''
    }
  },
  mounted () {
    this.fetchMetrics()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchMetrics () {
      try {
        this.loading = true
        this.error = null

        const response = await this.$http.get('/api/v1/system/metrics', {
          params: { mountpoint: this.mountpoint }
        })

        // Handle multiple response structures
        let data = null
        if (response) {
          if (response.data && response.data.disk) {
            data = response.data
          } else if (response.data && response.data.data && response.data.data.disk) {
            data = response.data.data
          } else if (response.disk) {
            data = response
          }
        }

        if (!data || !data.disk || !data.cpu || !data.ram) {
          throw new Error('Invalid response from server')
        }

        this.disk = data.disk
        this.cpu = data.cpu
        this.ram = data.ram
        this.lastUpdated = new Date()
      } catch (error) {
        console.error('Failed to fetch system metrics:', error)
        this.error = error.message || 'Failed to fetch metrics'
      } finally {
        this.loading = false
      }
    },

    refresh () {
      this.fetchMetrics()
      if (this.autoRefresh) {
        this.resetCountdown()
      }
    },

    startAutoRefresh () {
      this.resetCountdown()
      this.interval = setInterval(() => {
        this.fetchMetrics()
        this.resetCountdown()
      }, this.refreshInterval)
    },

    stopAutoRefresh () {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    resetCountdown () {
      this.nextRefreshIn = Math.floor(this.refreshInterval / 1000)
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
      this.countdownInterval = setInterval(() => {
        this.nextRefreshIn = Math.max(0, this.nextRefreshIn - 1)
      }, 1000)
    },

    formatBytes (bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatPercent (value) {
      if (value === null || value === undefined) return '0%'
      return value.toFixed(1) + '%'
    },

    formatLoad (value) {
      if (value === null || value === undefined) return '0.00'
      return value.toFixed(2)
    },

    formatTime (date) {
      if (!date) return ''
      return date.toLocaleTimeString()
    },

    getProgressClass (percent) {
      if (percent >= 90) return 'is-danger'
      if (percent >= 70) return 'is-warning'
      return 'is-success'
    }
  }
}
</script>

<style lang="scss" scoped>
.system-widget {
  padding: 1rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .is-dark & {
    background: #2c2c2c;
    color: #eee;
  }

  .system-widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .is-dark & {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }

  .system-widget-title {
    font-weight: 600;
    font-size: 1rem;
  }

  .system-widget-refresh-btn {
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: inherit;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      border-color: #555;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .system-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .metric-card {
    padding: 0.5rem 0.75rem;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 4px;

    .is-dark & {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  .metric-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.25rem;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .metric-body {
    .progress-container {
      position: relative;
      margin-bottom: 0.25rem;

      .progress {
        height: 1.25rem;
        border-radius: 4px;
      }

      .progress-label {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem;
        font-weight: bold;
        color: #333;
        text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff;

        .is-dark & {
          color: #fff;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        }
      }
    }

    .metric-detail {
      font-size: 0.75rem;
      color: #666;

      .is-dark & {
        color: #aaa;
      }

      .metric-available {
        color: #888;
        font-size: 0.7rem;

        .is-dark & {
          color: #999;
        }
      }
    }
  }

  .metric-footer {
    display: flex;
    justify-content: space-between;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.75rem;
    color: #888;

    .is-dark & {
      border-top-color: rgba(255, 255, 255, 0.1);
      color: #aaa;
    }

    .metric-footer-item {
      display: inline-block;
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
